/**
 * Helper function to clean Pydantic validation error prefixes
 * Removes "Value error, " prefix from error messages
 */
const cleanErrorMessage = (msg) => {
  if (typeof msg !== 'string') return msg;
  // Remove common Pydantic validation prefixes
  return msg.replace(/^Value error,\s*/i, '');
};

/**
 * Utility function to extract error messages from API responses
 * Handles various error formats from FastAPI including validation errors
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  // Check if error exists
  if (!error) return defaultMessage;

  // Check if it's a response error from axios
  if (error.response && error.response.data) {
    const { detail } = error.response.data;
    
    // If detail is a string, return it directly (cleaned)
    if (typeof detail === 'string') {
      return cleanErrorMessage(detail);
    }
    
    // If detail is an array (validation errors from FastAPI)
    if (Array.isArray(detail)) {
      // Extract the first error message or format multiple errors
      const messages = detail.map(err => {
        if (typeof err === 'string') return cleanErrorMessage(err);
        if (err.msg) return cleanErrorMessage(err.msg);
        if (err.message) return cleanErrorMessage(err.message);
        return 'Validation error';
      });
      return messages.length > 1
        ? `Multiple errors: ${messages.join(', ')}`
        : messages[0] || defaultMessage;
    }
    
    // If detail is an object with message properties
    if (typeof detail === 'object' && detail !== null) {
      if (detail.message) return detail.message;
      if (detail.msg) return detail.msg;
      if (detail.error) return detail.error;
      
      // Try to extract meaningful info from validation error objects
      if (detail.type && detail.msg) {
        return `${detail.type}: ${detail.msg}`;
      }
    }
    
    // Check for other common error properties
    if (error.response.data.message) {
      return error.response.data.message;
    }
    
    if (error.response.data.error) {
      return error.response.data.error;
    }
  }
  
  // Check for error message directly on error object
  if (error.message) {
    return error.message;
  }
  
  // Return default message if nothing else works
  return defaultMessage;
};

/**
 * Utility function to safely display error objects in UI
 * Prevents React from trying to render objects as children
 */
export const formatErrorForDisplay = (error) => {
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    return JSON.stringify(error);
  }
  return String(error);
};